import type { CSSProperties } from "react";
import { useInView } from "../landing/sections";

interface Trend {
  label: string;
  image: string;
  /** maturing trends are filled, emerging ones dashed */
  kind: "maturing" | "emerging";
  /** position inside the plot, in percent */
  x: number;
  y: number;
}

// Same five trends and icon files the Lite page has always used.
const TRENDS: Trend[] = [
  { label: "Robotics", image: "robotics", kind: "maturing", x: 16, y: 62 },
  { label: "Digital twins", image: "digital", kind: "emerging", x: 38, y: 26 },
  { label: "Edge analytics", image: "edge", kind: "maturing", x: 56, y: 56 },
  { label: "Synthetic data", image: "synthetic", kind: "emerging", x: 76, y: 80 },
  { label: "CoBots", image: "cobots", kind: "maturing", x: 86, y: 36 },
];

const vars = (v: Record<string, string | number>) => v as CSSProperties;

/** Trend bubbles by industry trend and organization readiness. Plays once the chart scrolls into view. */
export default function TrendsChart() {
  const basename = process.env.PUBLIC_URL || "";
  const [ref, active] = useInView<HTMLDivElement>(0.3);

  return (
    <div className={`rl-plotbox${active ? " is-active" : ""}`} ref={ref} aria-hidden="true">
      <div className="rl-plot">
        {/* A faint trajectory from "just starting" to "mainstream" that the bubbles sit along */}
        <svg className="rl-plot__path" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="rl-trajg" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="100" y2="0">
              <stop offset="0" stopColor="#17b0fd" />
              <stop offset="1" stopColor="#102478" />
            </linearGradient>
          </defs>
          <path d="M0 100 C 28 96, 52 70, 100 12" className="rl-plot__track" />
          <path d="M0 100 C 28 96, 52 70, 100 12" className="rl-plot__flow" stroke="url(#rl-trajg)" />
          <circle r="1.6" className="rl-plot__comet">
            <animateMotion dur="5s" repeatCount="indefinite" path="M0 100 C 28 96, 52 70, 100 12" />
          </circle>
        </svg>

        {TRENDS.map((t, i) => (
          <span
            key={t.label}
            className={`rl-bu rl-bu--${t.kind}`}
            style={vars({ left: `${t.x}%`, top: `${t.y}%`, "--i": i })}
          >
            <span className="rl-bu__c">
              <img src={`${basename}/images/${t.image}.webp`} alt="" />
              <b>{t.label}</b>
            </span>
          </span>
        ))}
      </div>
      <span className="rl-ax rl-ax--y">Industry trends</span>
      <span className="rl-ax rl-ax--x">Organization readiness</span>
    </div>
  );
}
